import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import {RootState} from '../../../../setup'
import {KTSVG} from '../../../../_metronic/helpers'
import {Breadcrumbs, Route} from '../../../components/Breadcrumbs'
import {formatDateTime, formatDate, formatMoney} from '../../../helper'
import {
  Budget,
  BudgetInitValues,
  BudgetSearchModelInitValues,
  BudgetTableModelInitValues,
} from '../models/BudgetModel'
import * as actions from '../redux/BudgetAction'
import {BudgetDelete} from './BudgetDelete'
import {ApprovalLegend} from '../../../components/ApprovalLegend'
import {Can} from '../../../rbac/Can'
import {Table} from '../../../components/ReactTable'
import {Field, Form, Formik} from 'formik'
import {DateRangePicker} from 'rsuite'
import * as Yup from 'yup'

type Props = {
  className: string
}

export const defaultColumnSizing = {
  size: 150,
  minSize: 20,
  maxSize: Number.MAX_SAFE_INTEGER,
}

export const Budgets: React.FC<Props> = ({className}) => {
  const dispatch = useDispatch()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [budgetToDelete, setBudgetToDelete] = useState<Budget>(BudgetInitValues)

  const Budgets: any = useSelector<RootState>((state) => state.budget.budgets, shallowEqual)
  const loading: any = useSelector<RootState>(({budget}) => budget.loading, shallowEqual)

  const pages: any = useSelector<RootState>(({budget}) => budget.pagination?.pages, shallowEqual)
  const [searchParams, setSearchParams]: any = useState({})
  const [createdAt, setCreatedAt]: any = useState([])
  const [approvedAt, setApprovedAt]: any = useState([])
  const [show, setShow] = useState(false)
  const filterHandel = () => {
    setShow(!show)
  }
  useEffect(() => {
    dispatch(actions.findAll(BudgetTableModelInitValues))
  }, [dispatch])

  const fetchData = useCallback(
    ({pageSize, pageIndex, params, sortBy}: any) => {
      const queryParams = {
        page: pageIndex + 1,
        sortBy: sortBy[0]?.id || 'createdAt',
        sortOrder: sortBy[0]?.desc === false ? 'asc' : 'desc',
        search: params,
      }
      dispatch(actions.findAll(queryParams))
    },
    [searchParams]
  )

  const columns = useMemo(
    () => [
      {
        Header: 'Created Date',
        accessor: 'createdAt',
        Cell: (params: any) => {
          if (params?.row?.original?.createdAt) {
            return formatDateTime(params?.row?.original?.createdAt)
          } else {
            return '-'
          }
        },
        show: true,
      },
      {
        Header: 'Created By',
        accessor: 'createdBy.fullName',
        Cell: (params: any) => {
          if (!params?.row?.original?.createdBy) return '-'
          return params?.row?.original?.createdBy.fullName
        },
        disableSortBy: true,
      },
      {
        Header: 'Budget Number',
        accessor: 'number',
        Cell: (params: any) => (
          <Link to={`/budgets/${params?.row?.original?._id}`}>{params?.row?.original?.number}</Link>
        ),
        show: true,
      },
      {
        Header: 'Estimated ',
        accessor: 'title',

        Cell: (params: any) => (
          <div>
            {params?.row?.original?.type === 'ESTIMATE' ? (
              <Link to={`/budgets/${params?.row?.original?._id}`}>
                {params?.row?.original?.title}
              </Link>
            ) : null}
          </div>
        ),
        disableSortBy: true,
      },
      {
        Header: 'Actual ',
        accessor: 'childrens[0].title',
        Cell: (params: any) => (
          <div>
            {params?.row?.original?.type === 'ESTIMATE' &&
            params?.row?.original?.childrens.length > 0 ? (
              <Link to={`/budgets/${params?.row?.original?.childrens[0]?._id}`}>
                {params?.row?.original?.childrens[0]?.title}
              </Link>
            ) : null}
          </div>
        ),
        disableSortBy: true,
      },
      {
        Header: 'Start Date - End Date',
        accessor: 'startDate',
        Cell: (params: any) => {
          const sDate = formatDate(params?.row?.original?.startDate)
          const eDate = formatDate(params?.row?.original?.endDate)
          return `${sDate} - ${eDate}`
        },
        disableSortBy: true,
      },
      {
        Header: 'Grand Total',
        accessor: 'total',
        Cell: (params: any) => {
          if (params?.row?.original?.total) {
            return formatMoney(params?.row?.original?.total)
          } else {
            return '-'
          }
        },
      },
      {
        Header: 'Sections',
        accessor: 'sections.length',
        show: true,
        disableSortBy: true,
      },
      {
        Header: 'Status',
        accessor: 'status',
        show: true,
      },

      {
        Header: 'Approved By',
        accessor: 'approvedBy.fullName',
        Cell: (params: any) => {
          if (!params?.row?.original?.approvedBy) return '-'
          return params?.row?.original?.approvedBy.fullName
        },
        disableSortBy: true,
      },
      {
        Header: 'Approved Date',
        accessor: 'approvedAt',
        Cell: (params: any) => {
          if (!params?.row?.original?.approvedBy) return '-'
          return formatDateTime(params?.row?.original?.approvedAt)
        },
        show: true,
      },

      {
        Header: 'Actions',
        Cell: (params: any) => (
          <div className='action-btn-wrapper d-flex flex-shrink-0'>
            <Can do='update' on='budgets'>
              {params?.row?.original?.approvedBy &&
              params?.row?.original?.childrens.length === 1 ? (
                <Link
                  to={`/budgets/${params?.row?.original?.childrens[0]?._id}/edit`}
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  title='Edit Budget'
                >
                  <KTSVG path='/media/icons/duotune/art/art005.svg' className='m-3 svg-icon-3' />
                </Link>
              ) : !params?.row?.original?.approvedBy && params?.row?.original?.type !== 'ACTUAL' ? (
                <Link
                  to={`/budgets/${params?.row?.original?._id}/edit`}
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  title='Edit Budget'
                >
                  <KTSVG path='/media/icons/duotune/art/art005.svg' className='m-3 svg-icon-3' />
                </Link>
              ) : (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-grey btn-sm'
                  title='Edit Budget'
                >
                  <KTSVG path='/media/icons/duotune/art/art005.svg' className='m-3 svg-icon-3' />
                </button>
              )}
            </Can>
            <Can do='approve' on='budgets'>
              {!params?.row?.original?.approvedBy &&
              params?.row?.original?.type === 'ESTIMATE' &&
              params?.row?.original?.status !== 'REJECTED' ? (
                <Link
                  to={`/budgets/${params?.row?.original?._id}/approve`}
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  title='Approve Budget'
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen026.svg'
                    className='ml-3 m-3 svg-icon-primary svg-icon-3'
                  />
                </Link>
              ) : params?.row?.original?.status === 'REJECTED' ? (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-grey btn-sm'
                  title='Rejected Budget'
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen026.svg'
                    className='ml-3 m-3 svg-icon-grey svg-icon-3'
                  />
                </button>
              ) : (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-success btn-sm'
                  title='Approved Budget'
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen026.svg'
                    className='ml-3 m-3 svg-icon-success svg-icon-3'
                  />
                </button>
              )}
            </Can>
            <Can do='create' on='budgets'>
              {params?.row?.original?.childrens?.length === 0 &&
              params?.row?.original?.type === 'ESTIMATE' &&
              params?.row?.original?.approvedBy ? (
                <Link
                  to={`/budgets/${params?.row?.original?._id}/actual`}
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  title='Create Actual Budget'
                >
                  <KTSVG
                    path='/media/icons/duotune/arrows/arr013.svg'
                    className='ml-3 m-3 svg-icon  svg-icon-3'
                  />
                </Link>
              ) : null}

              {params?.row?.original?.type === 'ESTIMATE' ? (
                <Link
                  to={`/budgets/${params?.row?.original?._id}/copy`}
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  title='Copy Budget'
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen054.svg'
                    className='ml-3 m-3 svg-icon-gray svg-icon-3'
                  />
                </Link>
              ) : null}
            </Can>
            <Can do='delete' on='budgets'>
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                data-bs-toggle='modal'
                data-bs-target='#kt_modal_delete_app'
                title='Delete Budget'
                onClick={() => {
                  setBudgetToDelete(params?.row?.original)
                  setShowDeleteDialog(true)
                }}
              >
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='m-3 svg-icon-3' />
              </button>
            </Can>
          </div>
        ),
      },
    ],
    []
  )

  const routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Budgets List', url: '/budgets'},
  ]

  return (
    <>
      <BudgetDelete
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false)
        }}
        budget={budgetToDelete}
      />
      <div className={`card card-custom ${className}`}>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Budgets List</span>
          </h3>
          <ApprovalLegend />
          <Can do='create' on='budgets'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Add Budget'
            >
              <Link to='/budgets/add' className='btn btn-sm btn-light-primary m-2'>
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Add Budget
              </Link>
            </div>
          </Can>
          <Can do='report' on='budgets'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Budget Reports'
            >
              <Link to='/budgets/report' className='btn btn-sm btn-light-primary'>
                <KTSVG path='/media/icons/duotune/files/fil001.svg' className='svg-icon-3' />
                Budget Report
              </Link>
            </div>
          </Can>
        </div>

        <div className='card-body'>
          <div
            className={`d-flex align-items-center ${
              !show ? 'justify-content-end' : 'justify-content-between'
            }`}
          >
            <p className='my-0 me-3' style={{color: '#004B87', fontSize: '14px'}}>
              <b>Search Filters</b>
            </p>
            <span onClick={filterHandel}>
              <img src='/media/svg/icons/General/filter.svg' alt='' />
            </span>
          </div>
          {show ? (
            <Formik
              initialValues={BudgetSearchModelInitValues}
              validationSchema={Yup.object({
                number: Yup.string(),
                status: Yup.string(),
                createdAt: Yup.array(Yup.string()),
                approvedAt: Yup.array(Yup.string()),
                approvedBy: Yup.string(),
              })}
              onSubmit={(values: any) => {
                if (values.status === 'Select Status') {
                  values.status = ''
                }

                setSearchParams(values)
              }}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5'>
                    <div className='form-group col'>
                      <label htmlFor='createdAt' className='fw-bold'>
                        Created Date
                      </label>
                      <DateRangePicker
                        value={createdAt}
                        block
                        name='createdAt'
                        cleanable={true}
                        size='md'
                        appearance='default'
                        character='-'
                        showOneCalendar
                        placeholder='Select Date'
                        format='dd/MM/yyyy'
                        onClean={(value: any) => {
                          setFieldValue('createdAt', '')
                        }}
                        onChange={(value: any) => {
                          setFieldValue('createdAt', value)
                          setCreatedAt(value)
                        }}
                      />
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Budget Number
                      </label>
                      <Field
                        name='number'
                        placeholder='Budget Number'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='status' className='fw-bold'>
                        Status
                      </label>
                      <Field
                        as='select'
                        className='form-select form-select-md'
                        data-control='select2'
                        name='status'
                      >
                        <option>Status</option>
                        <option value='NEW'>NEW</option>
                        <option value='APPROVED'>APPROVED</option>
                        <option value='COMPLETED'>COMPLETED</option>
                        <option value='CANCELLED'>CANCELLED</option>
                        <option value='REJECTED'>REJECTED</option>
                      </Field>
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='approvedAt' className='fw-bold'>
                        Approved Date
                      </label>
                      <DateRangePicker
                        value={approvedAt}
                        block
                        name='approvedAt'
                        cleanable={true}
                        size='md'
                        appearance='default'
                        character='-'
                        showOneCalendar
                        placeholder='Select Date'
                        format='dd/MM/yyyy'
                        onClean={(value: any) => {
                          setFieldValue('approvedAt', '')
                        }}
                        onChange={(value: any) => {
                          setFieldValue('approvedAt', value)
                          setApprovedAt(value)
                        }}
                      />
                    </div>
                  </div>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Approved By
                      </label>
                      <Field
                        name='approvedBy'
                        placeholder='Approved By'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>
                  <div className='d-flex justify-content-end mt-10'>
                    <Link
                      to='/budgets'
                      className='btn btn-sm btn-light-primary me-3'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Cancel'
                      onClick={() => {
                        setSearchParams(BudgetSearchModelInitValues)
                        setFieldValue('number', '')
                        setFieldValue('status', '')
                      }}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </Link>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Search'
                      id='liveToastBtn'
                    >
                      <span className='indicator-label'>Search</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : null}
          <div className='d-flex mt-5 '>
            <div className='ag-theme-material' style={{width: '100%'}}>
              <Table
                columns={columns}
                data={Budgets}
                params={searchParams}
                pageCount={pages}
                fetchData={fetchData}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
